import { status as AppointmentStatuses } from '@spike/appointment-model';
import { Order, RedeemTypes, SaveOrderItemRedeem } from '@spike/order-model';
import moment, { Moment } from 'moment-timezone';

export const collectEnrollmentRedeemsFromOrder = (order: Order): Map<number, number> => {
    const map = new Map<number, number>();
    for (const ai of order.items?.appointments ?? []) {
        const orderItemId = ai.orderItem?.id;
        const redeem = ai.orderItem?.redeem as { type?: RedeemTypes; id?: number } | undefined;
        if (orderItemId != null && redeem?.type === RedeemTypes.ENROLLMENT && redeem.id != null) {
            map.set(orderItemId, redeem.id);
        }
    }
    return map;
};

export const buildEnrollmentRedeemsForSavePatch = (
    snapshot: Map<number, number> | null | undefined,
    pending: { orderItemId: number; enrollmentId: number }
): SaveOrderItemRedeem[] => {
    const byOrderItemId = new Map<number, number>(snapshot ?? []);
    byOrderItemId.set(pending.orderItemId, pending.enrollmentId);
    return Array.from(byOrderItemId.entries()).map(([orderItemId, enrollmentId]) => ({
        orderItemId,
        redeem: { type: RedeemTypes.ENROLLMENT, id: enrollmentId }
    }));
};

export type PendingEnrollmentRedeemPayload = {
    orderId: number;
    enrollmentId: number;
    petId: number;
    serviceId: number;
    bookedAt: Moment;
    createdAppointmentId?: number;
};

export const findAppointmentOrderItemForRedeem = (
    orderLoaded: Order,
    pending: PendingEnrollmentRedeemPayload,
    businessTimeZone: string
): (typeof orderLoaded.items.appointments)[number] | undefined => {
    type Oi = (typeof orderLoaded.items.appointments)[number];
    const appointments = orderLoaded.items?.appointments ?? [];

    const isNoShowOrCanceled = (oi: Oi) => {
        const id = oi.appointment.status?.id;
        return id === AppointmentStatuses.NO_SHOW || id === AppointmentStatuses.CANCELLED;
    };

    if (pending.createdAppointmentId != null) {
        for (const oi of appointments) {
            if (Number(oi.appointment.id) !== Number(pending.createdAppointmentId)) continue;
            if (isNoShowOrCanceled(oi)) continue;
            return oi;
        }
        return undefined;
    }

    const samePet = (oi: Oi) => Number(oi.pet?.id ?? oi.appointment.pet?.id) === Number(pending.petId);

    const sameService = (oi: Oi) =>
        (oi.appointment.services ?? []).some(s => Number(s.id) === Number(pending.serviceId));

    const pendingDay = moment(pending.bookedAt).tz(businessTimeZone).format('YYYY-MM-DD');

    const sameCalendarDay = (when: Moment) => moment(when).tz(businessTimeZone).format('YYYY-MM-DD') === pendingDay;

    for (const oi of appointments) {
        if (!samePet(oi) || !sameService(oi) || isNoShowOrCanceled(oi)) continue;
        const when = oi.appointment.duration?.from ?? oi.orderItem.startedAt;
        if (when != null && sameCalendarDay(when)) {
            return oi;
        }
    }
    return undefined;
};

export const ENROLLMENT_REDEEM_GET_ORDER_MAX_POLLS = 60;
export const ENROLLMENT_REDEEM_GET_ORDER_POLL_MS = 1000;
